// btw-front/src/components/admin/dashboard/StatsTab.jsx
// [통계] 탭 — 전사 요약 집계 + 고객사 검색 데이터셋 카드
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatCard } from "../../ui/StatCard";
import { Badge } from "../../ui/Badge";
import { EmptyState } from "../../ui/EmptyState";
import * as adminStats from "../../../api/adminStats";
import { formatKRW } from "./shared";
import CompanyDatasetCard from "./CompanyDatasetCard";

export default function StatsTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminStats
      .get()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>;
  }
  if (!data) {
    return <EmptyState title={t("admin.empty")} />;
  }

  const { deals, partners, signups } = data;
  // 분포 막대 길이 기준 — 최대 단계 건수(0 분모 방지로 최소 1)
  const maxStage = Math.max(1, ...deals.byStage.map((s) => s.value));

  return (
    <div>
      {/* 전사 KPI */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label={t("admin.stats.kpi.partners")} value={partners.total} />
        <StatCard label={t("admin.stats.kpi.activePartners")} value={partners.active} />
        <StatCard label={t("admin.stats.kpi.deals")} value={deals.total} />
        <StatCard label={t("admin.stats.kpi.inProgress")} value={deals.inProgress} />
        <StatCard
          accent
          label={t("admin.stats.kpi.poTotal")}
          value={`${formatKRW(deals.poTotal)} ${t("common.unitKrw")}`}
        />
      </section>

      {/* 가입 신청 현황 */}
      <section className="mt-6">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">
          {t("admin.stats.sectionSignups")}
        </h2>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label={t("admin.stats.signup.pending")} value={signups.pending} />
          <StatCard label={t("admin.stats.signup.approved")} value={signups.approved} />
          <StatCard label={t("admin.stats.signup.rejected")} value={signups.rejected} />
        </div>
      </section>

      {/* 영업단계별 딜 분포 */}
      <section className="mt-6">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">
          {t("admin.stats.sectionByStage")}
        </h2>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                  {t("admin.stats.table.stage")}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t("admin.stats.table.distribution")}
                </th>
                <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                  {t("admin.stats.table.count")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deals.byStage.map((s) => (
                <tr key={s.status}>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <Badge tone={s.status}>{t(`partners.deals.status.${s.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="h-2 w-full rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-accent"
                        style={{ width: `${Math.round((s.value / maxStage) * 100)}%` }}
                      />
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums text-slate-900">
                    {s.value} {t("common.unitCount")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 고객사 검색 데이터셋(국민연금 월별) 활성 상태 + 즉시 갱신 */}
      <section className="mt-6">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">
          {t("admin.stats.datasetSection")}
        </h2>
        <CompanyDatasetCard />
      </section>
    </div>
  );
}
